import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownToLine, Loader2, CheckCircle } from 'lucide-react';
import { productsApi, inventoryApi } from '@/api/client';
import type { Product, StockInForm } from '@/types';
import { format } from 'date-fns';

export function StockInPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [existingLots, setExistingLots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLots, setIsLoadingLots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lotInputMode, setLotInputMode] = useState<'select' | 'new'>('new');
  const navigate = useNavigate();

  const today = format(new Date(), 'yyyy-MM-dd');

  const [form, setForm] = useState<StockInForm>({
    product_id: 0,
    lot_number: '',
    quantity: 0,
    expiry_date: '',
    received_date: today,
    note: '',
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (form.product_id) {
      fetchExistingLots(form.product_id);
    } else {
      setExistingLots([]);
      setLotInputMode('new');
    }
  }, [form.product_id]);

  const fetchProducts = async () => {
    const response = await productsApi.list({ limit: 100 });
    if (response.success && response.data) {
      setProducts(response.data.items);
    }
    setIsLoading(false);
  };

  const fetchExistingLots = async (productId: number) => {
    setIsLoadingLots(true);
    const response = await inventoryApi.fifo(productId);
    if (response.success && response.data) {
      // 重複を除いたロット番号のリストを作成
      const lots = [...new Set(response.data.map(inv => inv.lot_number))];
      setExistingLots(lots);
      // 既存ロットがあれば選択モードをデフォルトに
      if (lots.length > 0) {
        setLotInputMode('select');
        setForm(f => ({ ...f, lot_number: lots[0] }));
      } else {
        setLotInputMode('new');
        setForm(f => ({ ...f, lot_number: '' }));
      }
    }
    setIsLoadingLots(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // 整数チェック
    if (!Number.isInteger(form.quantity) || form.quantity <= 0) {
      setError('数量は1以上の整数を入力してください');
      setIsSubmitting(false);
      return;
    }

    const submitData: StockInForm = {
      ...form,
      expiry_date: form.expiry_date || undefined,
    };

    const response = await inventoryApi.stockIn(submitData);

    if (response.success) {
      setSuccess(true);
      // ロットリストを再取得
      if (form.product_id) {
        fetchExistingLots(form.product_id);
      }
      setTimeout(() => {
        setSuccess(false);
        // Reset form but keep product and date
        setForm({
          ...form,
          lot_number: '',
          quantity: 0,
          expiry_date: '',
          note: '',
        });
      }, 2000);
    } else {
      setError(response.error || '入庫登録に失敗しました');
    }
    setIsSubmitting(false);
  };

  const selectedProduct = products.find(p => p.id === form.product_id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg">
          <ArrowDownToLine className="h-6 w-6 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">入庫登録</h1>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">入庫を登録しました</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Product Selection */}
        <div>
          <label className="label">商品 *</label>
          <select
            required
            value={form.product_id || ''}
            onChange={(e) => setForm({ ...form, product_id: parseInt(e.target.value) || 0, lot_number: '' })}
            className="input"
          >
            <option value="">商品を選択してください</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.unit})
              </option>
            ))}
          </select>
          {selectedProduct && (
            <p className="text-sm text-gray-500 mt-1">
              期限アラート: {selectedProduct.expiry_alert_days}日前
            </p>
          )}
        </div>

        {/* Lot Number */}
        <div>
          <label className="label">ロット番号 *</label>
          
          {/* 選択/新規入力の切り替え（既存ロットがある場合のみ表示） */}
          {existingLots.length > 0 && (
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="lotInputMode"
                  checked={lotInputMode === 'select'}
                  onChange={() => {
                    setLotInputMode('select');
                    setForm({ ...form, lot_number: existingLots[0] || '' });
                  }}
                  className="text-primary-600"
                />
                既存から選択
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="lotInputMode"
                  checked={lotInputMode === 'new'}
                  onChange={() => {
                    setLotInputMode('new');
                    setForm({ ...form, lot_number: '' });
                  }}
                  className="text-primary-600"
                />
                新規入力
              </label>
            </div>
          )}

          {isLoadingLots ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-gray-500">ロット情報を取得中...</span>
            </div>
          ) : lotInputMode === 'select' && existingLots.length > 0 ? (
            <select
              required
              value={form.lot_number}
              onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
              className="input"
            >
              {existingLots.map((lot) => (
                <option key={lot} value={lot}>{lot}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              required
              value={form.lot_number}
              onChange={(e) => setForm({ ...form, lot_number: e.target.value })}
              className="input"
              placeholder="例: LOT-2024-001"
            />
          )}
          
          <p className="text-sm text-gray-500 mt-1">
            {lotInputMode === 'select' 
              ? '同じロット番号の在庫に数量が加算されます'
              : '新しいロットとして登録されます（同じ番号があれば加算）'}
          </p>
        </div>

        {/* Quantity */}
        <div>
          <label className="label">数量 *</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              required
              min="1"
              step="1"
              value={form.quantity || ''}
              onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
              className="input"
              placeholder="0"
            />
            {selectedProduct && (
              <span className="text-gray-500 whitespace-nowrap">
                {selectedProduct.unit}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            ※ 整数で入力してください
          </p>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">入庫日 *</label>
            <input
              type="date"
              required
              value={form.received_date}
              onChange={(e) => setForm({ ...form, received_date: e.target.value })}
              className="input"
            />
          </div>

          <div>
            <label className="label">賞味期限</label>
            <input
              type="date"
              value={form.expiry_date || ''}
              onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              className="input"
            />
          </div>
        </div>

        {/* Note */}
        <div>
          <label className="label">メモ</label>
          <textarea
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="input"
            rows={2}
            placeholder="仕入先、備考など（任意）"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !form.product_id || !form.lot_number || !form.quantity}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <ArrowDownToLine className="h-4 w-4" />
                入庫登録
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
